import { StreamTextUtils } from "./StreamTextUtils";

const BLUEBERRY_API_V1_BASE = "https://blueberry.cerebralzip.com/api/v1";

export class PlannerStreamClient {
  constructor({ streamTextUtils } = {}) {
    this.streamTextUtils = streamTextUtils || new StreamTextUtils();
  }

  async stream({
    message,
    chatSessionId,
    messages,
    gadType,
    language,
    useMemory,
    outputLang,
    token,
    onDelta,
    onDone,
    onError,
  }) {
    try {
      const url = `${BLUEBERRY_API_V1_BASE}/chat_gad_router/${encodeURIComponent(
        chatSessionId || "default",
      )}`;

      const payload = {
        messages:
          Array.isArray(messages) && messages.length > 0
            ? messages
            : [
                {
                  role: "user",
                  content: message,
                  annotations: null,
                  metadata: {},
                },
              ],
        gad_type: Array.isArray(gadType) && gadType.length ? gadType : [""],
        output_lang: outputLang || "en",
        language: language || "ma",
        use_memory: typeof useMemory === "boolean" ? useMemory : true,
        action_type: "PLAN",
        data: null,
      };

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.body) {
        const text = await res.text().catch(() => "");
        const parsed = this._parsePlannerWireFormat(text);
        onDelta?.({
          text: parsed.text,
          thinking: parsed.thinking,
          contextArray: parsed.contextArray,
        });
        onDone?.({ contextArray: parsed.contextArray });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let contextArray = [];
      let gotSources = false;
      let started = false;
      let currentThinking = "";
      let currentText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const sourcesExtract = this._tryExtractSourcesFromBuffer(buffer);
        if (sourcesExtract?.contextArray?.length) {
          contextArray = sourcesExtract.contextArray;
          gotSources = true;
        }

        const parsed = this._parsePlannerWireFormat(buffer);
        currentThinking = parsed.thinking;
        currentText = this.streamTextUtils.stripTrailingSourcesPayload(
          parsed.text,
        );

        // Only start streaming once we have something meaningful to show
        if (!started && (currentText || currentThinking || gotSources))
          started = true;
        if (!started) continue;

        onDelta?.({
          text: currentText,
          thinking: currentThinking || undefined,
          contextArray,
        });
      }

      // Final pass (in case sources arrived at the end)
      if (!gotSources) {
        const sourcesExtract = this._tryExtractSourcesFromBuffer(buffer);
        if (sourcesExtract?.contextArray?.length)
          contextArray = sourcesExtract.contextArray;
      }

      onDone?.({ contextArray });
    } catch (e) {
      onError?.(e);
    }
  }

  _tryExtractSourcesFromBuffer(raw) {
    const text = typeof raw === "string" ? raw : "";
    const idx = text.lastIndexOf("sources:");
    if (idx >= 0) {
      const after = text.slice(idx + "sources:".length);
      const trimmed = after.trimStart();

      // Preferred: use shared leading-JSON extractor
      const extracted = this.streamTextUtils.extractLeadingJSON(trimmed);
      if (extracted) {
        const sources = extracted.obj?.sources;
        const contextArray =
          this.streamTextUtils.indexSourcesByContextId(sources);
        return { contextArray };
      }

      // Fallback: brace-match the first JSON object after "sources:"
      const jsonStart = trimmed.indexOf("{");
      if (jsonStart < 0) return null;

      const candidate = trimmed.slice(jsonStart);
      const jsonStr = this._extractFirstJSONObject(candidate);
      if (!jsonStr) return null;

      try {
        const obj = JSON.parse(jsonStr);
        const sources = obj?.sources;
        const contextArray =
          this.streamTextUtils.indexSourcesByContextId(sources);
        return { contextArray };
      } catch {
        return null;
      }
    }

    // Alternate planner format: trailing raw JSON object `{ "sources": [...] }`
    const trailing = this.streamTextUtils.extractTrailingSourcesJSON?.(text);
    if (!trailing) return null;
    const contextArray = this.streamTextUtils.indexSourcesByContextId(
      trailing.obj?.sources,
    );
    return { contextArray };
  }

  _extractFirstJSONObject(text) {
    if (!text) return null;
    let i = 0;
    while (i < text.length && /\s/.test(text[i])) i++;
    if (i >= text.length || text[i] !== "{") return null;

    let depth = 0;
    let inString = false;
    let escape = false;
    const start = i;

    for (; i < text.length; i++) {
      const ch = text[i];
      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === "\\") {
          escape = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }

      if (ch === '"') inString = true;
      else if (ch === "{") depth++;
      else if (ch === "}") {
        depth--;
        if (depth === 0) return text.slice(start, i + 1);
      }
    }
    return null;
  }

  _parsePlannerWireFormat(raw) {
    const text = typeof raw === "string" ? raw : "";
    // Collect all memory chunks (planner "thinking")
    const memRe = /^memory:\s*"(.*)"/gm;
    let memMatch;
    const memoryParts = [];
    while ((memMatch = memRe.exec(text)) !== null) {
      memoryParts.push(memMatch[1]);
    }
    const thinking = this.streamTextUtils.normalizeApiText(
      memoryParts.join(""),
    );

    // Visible planner content should come from 0: "..." lines
    const zeroRe = /^0:\s*"(.*)"/gm;
    let zeroMatch;
    const zeroParts = [];
    while ((zeroMatch = zeroRe.exec(text)) !== null) {
      zeroParts.push(zeroMatch[1]);
    }
    const answer = this.streamTextUtils.stripTrailingSourcesPayload(
      this.streamTextUtils.normalizeApiText(zeroParts.join("")),
    );

    return { thinking, text: answer, contextArray: [] };
  }
}
